import { useRef, useEffect, useState, useCallback } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Projectile {
	x: number;
	y: number;
	vx: number;
	vy: number;
	radius: number;
	trail: { x: number; y: number }[];
	active: boolean;
	landed: boolean;
}

interface Target {
	x: number;
	y: number;
	width: number;
	height: number;
	hit: boolean;
	type: "tower" | "flag" | "wall";
}

interface GameState {
	armLength: number; // 0.3 - 1.0 (normalized)
	tension: number; // 0.3 - 1.0 (normalized)
	armAngle: number; // current angle in radians
	isLoaded: boolean;
	isFiring: boolean;
	projectile: Projectile | null;
	targets: Target[];
	score: number;
	totalShots: number;
	wind: number; // wind force
	groundY: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const GRAVITY = 600;
const ARM_PIVOT_X_RATIO = 0.22;
const ARM_PIVOT_Y_OFFSET = 80;
const BASE_ARM_LENGTH = 180;
const MAX_ARM_LENGTH = 320;
const ANGULAR_VELOCITY_BASE = 8;
const RELEASE_ANGLE = -Math.PI / 4;
const LOADED_ANGLE = Math.PI / 3.2;

// ─── Renaissance Drawing Helpers ─────────────────────────────────────────────

function drawInkLine(
	ctx: CanvasRenderingContext2D,
	x1: number,
	y1: number,
	x2: number,
	y2: number,
	width: number = 2,
	alpha: number = 0.85
) {
	ctx.save();
	ctx.strokeStyle = `rgba(50, 30, 10, ${alpha})`;
	ctx.lineWidth = width;
	ctx.lineCap = "round";
	ctx.lineJoin = "round";

	// Slightly wobbly line for hand-drawn feel
	ctx.beginPath();
	ctx.moveTo(x1, y1);
	const segments = Math.max(3, Math.floor(Math.hypot(x2 - x1, y2 - y1) / 20));
	for (let i = 1; i <= segments; i++) {
		const t = i / segments;
		const wobble = width * 0.3;
		const wx = x1 + (x2 - x1) * t + (Math.random() - 0.5) * wobble;
		const wy = y1 + (y2 - y1) * t + (Math.random() - 0.5) * wobble;
		ctx.lineTo(wx, wy);
	}
	ctx.stroke();
	ctx.restore();
}

function drawInkCircle(
	ctx: CanvasRenderingContext2D,
	cx: number,
	cy: number,
	radius: number,
	width: number = 2,
	fill: boolean = false
) {
	ctx.save();
	ctx.strokeStyle = "rgba(50, 30, 10, 0.85)";
	ctx.lineWidth = width;
	ctx.lineCap = "round";

	ctx.beginPath();
	const points = 32;
	for (let i = 0; i <= points; i++) {
		const angle = (i / points) * Math.PI * 2;
		const wobble = radius * 0.03;
		const r = radius + (Math.random() - 0.5) * wobble;
		const x = cx + Math.cos(angle) * r;
		const y = cy + Math.sin(angle) * r;
		if (i === 0) ctx.moveTo(x, y);
		else ctx.lineTo(x, y);
	}
	ctx.closePath();
	ctx.stroke();

	if (fill) {
		ctx.fillStyle = "rgba(50, 30, 10, 0.15)";
		ctx.fill();
	}
	ctx.restore();
}

function drawMirroredText(
	ctx: CanvasRenderingContext2D,
	text: string,
	x: number,
	y: number,
	size: number = 14,
	mirrored: boolean = true
) {
	ctx.save();
	ctx.font = `italic ${size}px 'Georgia', 'Times New Roman', serif`;
	ctx.fillStyle = "rgba(50, 30, 10, 0.7)";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	if (mirrored) {
		ctx.translate(x, y);
		ctx.scale(-1, 1);
		ctx.fillText(text, 0, 0);
	} else {
		ctx.fillText(text, x, y);
	}
	ctx.restore();
}

function drawSketchRect(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	w: number,
	h: number,
	lineWidth: number = 1.5
) {
	drawInkLine(ctx, x, y, x + w, y, lineWidth);
	drawInkLine(ctx, x + w, y, x + w, y + h, lineWidth);
	drawInkLine(ctx, x + w, y + h, x, y + h, lineWidth);
	drawInkLine(ctx, x, y + h, x, y, lineWidth);
}

// ─── Parchment Background ────────────────────────────────────────────────────

function drawParchment(ctx: CanvasRenderingContext2D, w: number, h: number) {
	// Base parchment color
	const gradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.7);
	gradient.addColorStop(0, "#f4e4c1");
	gradient.addColorStop(0.5, "#e8d5a8");
	gradient.addColorStop(1, "#d4b978");
	ctx.fillStyle = gradient;
	ctx.fillRect(0, 0, w, h);

	// Aged stains and texture
	ctx.save();
	for (let i = 0; i < 12; i++) {
		const sx = Math.random() * w;
		const sy = Math.random() * h;
		const sr = 30 + Math.random() * 120;
		const stainGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
		stainGrad.addColorStop(0, "rgba(160, 120, 60, 0.06)");
		stainGrad.addColorStop(1, "rgba(160, 120, 60, 0)");
		ctx.fillStyle = stainGrad;
		ctx.fillRect(sx - sr, sy - sr, sr * 2, sr * 2);
	}

	// Dark edges for aged look
	const edgeGrad = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.75);
	edgeGrad.addColorStop(0, "rgba(100, 70, 30, 0)");
	edgeGrad.addColorStop(1, "rgba(80, 50, 20, 0.25)");
	ctx.fillStyle = edgeGrad;
	ctx.fillRect(0, 0, w, h);

	// Subtle fiber texture lines
	ctx.strokeStyle = "rgba(140, 110, 60, 0.04)";
	ctx.lineWidth = 1;
	for (let i = 0; i < 80; i++) {
		const y = Math.random() * h;
		ctx.beginPath();
		ctx.moveTo(0, y);
		ctx.lineTo(w, y + (Math.random() - 0.5) * 8);
		ctx.stroke();
	}
	ctx.restore();
}

// ─── Ground Drawing ──────────────────────────────────────────────────────────

function drawGround(ctx: CanvasRenderingContext2D, w: number, groundY: number) {
	// Ground line
	ctx.save();
	ctx.strokeStyle = "rgba(50, 30, 10, 0.6)";
	ctx.lineWidth = 2.5;
	ctx.beginPath();
	ctx.moveTo(0, groundY);
	for (let x = 0; x < w; x += 5) {
		ctx.lineTo(x, groundY + (Math.random() - 0.5) * 2);
	}
	ctx.stroke();

	// Grass tufts
	ctx.strokeStyle = "rgba(50, 30, 10, 0.3)";
	ctx.lineWidth = 1;
	for (let x = 0; x < w; x += 15 + Math.random() * 20) {
		const blades = 2 + Math.floor(Math.random() * 3);
		for (let b = 0; b < blades; b++) {
			ctx.beginPath();
			ctx.moveTo(x + b * 3, groundY);
			ctx.quadraticCurveTo(
				x + b * 3 + (Math.random() - 0.5) * 8,
				groundY - 8 - Math.random() * 10,
				x + b * 3 + (Math.random() - 0.5) * 6,
				groundY - 12 - Math.random() * 8
			);
			ctx.stroke();
		}
	}
	ctx.restore();
}

// ─── Catapult Drawing ────────────────────────────────────────────────────────

function drawCatapult(
	ctx: CanvasRenderingContext2D,
	pivotX: number,
	groundY: number,
	armLength: number,
	armAngle: number,
	tension: number,
	isLoaded: boolean
) {
	const baseWidth = 140;
	const baseHeight = 30;
	const frameHeight = 90;
	const baseX = pivotX - baseWidth / 2;
	const baseY = groundY - baseHeight;

	// ─── Base platform (wooden frame) ───
	drawSketchRect(ctx, baseX, baseY, baseWidth, baseHeight, 2.5);

	// Cross-hatching on base for wood texture
	ctx.save();
	ctx.strokeStyle = "rgba(50, 30, 10, 0.2)";
	ctx.lineWidth = 0.8;
	for (let x = baseX + 8; x < baseX + baseWidth; x += 12) {
		ctx.beginPath();
		ctx.moveTo(x, baseY + 4);
		ctx.lineTo(x - 6, baseY + baseHeight - 4);
		ctx.stroke();
	}
	ctx.restore();

	// Wheels
	const wheelR = 16;
	drawInkCircle(ctx, baseX + 20, groundY + 2, wheelR, 2);
	drawInkCircle(ctx, baseX + 20, groundY + 2, 4, 1.5, true);
	drawInkCircle(ctx, baseX + baseWidth - 20, groundY + 2, wheelR, 2);
	drawInkCircle(ctx, baseX + baseWidth - 20, groundY + 2, 4, 1.5, true);

	// Wheel spokes
	for (let i = 0; i < 6; i++) {
		const a = (i / 6) * Math.PI * 2;
		for (const wx of [baseX + 20, baseX + baseWidth - 20]) {
			drawInkLine(
				ctx,
				wx,
				groundY + 2,
				wx + Math.cos(a) * (wheelR - 3),
				groundY + 2 + Math.sin(a) * (wheelR - 3),
				1,
				0.4
			);
		}
	}

	// ─── Vertical frame supports ───
	const frameTop = baseY - frameHeight;
	drawInkLine(ctx, pivotX - 20, baseY, pivotX - 8, frameTop, 3, 0.8);
	drawInkLine(ctx, pivotX + 20, baseY, pivotX + 8, frameTop, 3, 0.8);

	// Cross brace on frame
	drawInkLine(ctx, pivotX - 15, baseY - frameHeight * 0.4, pivotX + 15, baseY - frameHeight * 0.4, 1.5, 0.5);

	const pivotY = frameTop + 5;

	// ─── Pivot point ───
	drawInkCircle(ctx, pivotX, pivotY, 8, 2.5, true);
	drawInkCircle(ctx, pivotX, pivotY, 3, 2, true);

	// ─── Torsion spring coils ───
	ctx.save();
	ctx.strokeStyle = `rgba(50, 30, 10, ${0.4 + tension * 0.4})`;
	ctx.lineWidth = 1.5 + tension;
	const coils = 5 + Math.floor(tension * 4);
	const coilRadius = 10 + tension * 5;
	ctx.beginPath();
	for (let i = 0; i < coils; i++) {
		const t = i / coils;
		const cx = pivotX + Math.cos(armAngle + Math.PI) * (12 + t * 8);
		const cy = pivotY + Math.sin(armAngle + Math.PI) * (12 + t * 8);
		const cr = coilRadius * (0.5 + t * 0.5);
		if (i === 0) ctx.moveTo(cx + cr * 0.3, cy);
		ctx.arc(cx, cy, cr * 0.3, 0, Math.PI * 1.5, false);
	}
	ctx.stroke();
	ctx.restore();

	// ─── Catapult arm ───
	const armEndX = pivotX + Math.cos(armAngle) * armLength;
	const armEndY = pivotY + Math.sin(armAngle) * armLength;
	const armBackX = pivotX + Math.cos(armAngle + Math.PI) * (armLength * 0.3);
	const armBackY = pivotY + Math.sin(armAngle + Math.PI) * (armLength * 0.3);

	// Arm shadow
	ctx.save();
	ctx.strokeStyle = "rgba(50, 30, 10, 0.15)";
	ctx.lineWidth = 8;
	ctx.lineCap = "round";
	ctx.beginPath();
	ctx.moveTo(armBackX + 2, armBackY + 2);
	ctx.lineTo(armEndX + 2, armEndY + 2);
	ctx.stroke();
	ctx.restore();

	// Main arm
	drawInkLine(ctx, armBackX, armBackY, armEndX, armEndY, 4, 0.9);

	// Arm wood grain lines
	const perpX = Math.cos(armAngle + Math.PI / 2) * 2;
	const perpY = Math.sin(armAngle + Math.PI / 2) * 2;
	drawInkLine(
		ctx,
		armBackX + perpX,
		armBackY + perpY,
		armEndX + perpX,
		armEndY + perpY,
		1,
		0.25
	);
	drawInkLine(
		ctx,
		armBackX - perpX,
		armBackY - perpY,
		armEndX - perpX,
		armEndY - perpY,
		1,
		0.25
	);

	// ─── Sling/bucket at arm end ───
	const bucketSize = 14;
	ctx.save();
	ctx.strokeStyle = "rgba(50, 30, 10, 0.7)";
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.arc(armEndX, armEndY, bucketSize, armAngle - 0.3, armAngle + Math.PI + 0.3, false);
	ctx.stroke();
	ctx.restore();

	// ─── Counterweight at back ───
	const cwSize = 18 + tension * 12;
	ctx.save();
	ctx.fillStyle = "rgba(50, 30, 10, 0.2)";
	ctx.strokeStyle = "rgba(50, 30, 10, 0.7)";
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.arc(armBackX, armBackY, cwSize, 0, Math.PI * 2);
	ctx.fill();
	ctx.stroke();

	// Cross-hatch on counterweight
	ctx.strokeStyle = "rgba(50, 30, 10, 0.15)";
	ctx.lineWidth = 0.8;
	for (let i = -cwSize; i < cwSize; i += 4) {
		ctx.beginPath();
		const h = Math.sqrt(Math.max(0, cwSize * cwSize - i * i));
		ctx.moveTo(armBackX + i, armBackY - h);
		ctx.lineTo(armBackX + i, armBackY + h);
		ctx.stroke();
	}
	ctx.restore();

	// ─── Projectile (stone) if loaded ───
	if (isLoaded) {
		const stoneX = armEndX + Math.cos(armAngle + Math.PI / 2) * 8;
		const stoneY = armEndY + Math.sin(armAngle + Math.PI / 2) * 8;
		drawInkCircle(ctx, stoneX, stoneY, 8, 2, true);
	}

	// ─── Rope bindings on arm ───
	for (let i = 0.2; i <= 0.8; i += 0.3) {
		const rx = pivotX + Math.cos(armAngle) * armLength * i;
		const ry = pivotY + Math.sin(armAngle) * armLength * i;
		ctx.save();
		ctx.strokeStyle = "rgba(50, 30, 10, 0.35)";
		ctx.lineWidth = 1;
		for (let j = 0; j < 3; j++) {
			const offset = (j - 1) * 3;
			const px = Math.cos(armAngle + Math.PI / 2) * (4 + offset);
			const py = Math.sin(armAngle + Math.PI / 2) * (4 + offset);
			ctx.beginPath();
			ctx.moveTo(rx - px, ry - py);
			ctx.lineTo(rx + px, ry + py);
			ctx.stroke();
		}
		ctx.restore();
	}

	return { pivotX, pivotY, armEndX, armEndY };
}

// ─── Target Drawing ──────────────────────────────────────────────────────────

function drawTarget(ctx: CanvasRenderingContext2D, target: Target, groundY: number) {
	const { x, y, width, height, hit, type } = target;

	ctx.save();
	if (hit) {
		ctx.globalAlpha = 0.35;
	}

	if (type === "tower") {
		// Stone tower
		drawSketchRect(ctx, x, y, width, height, 2);
		// Battlements
		const bw = width / 4;
		for (let i = 0; i < 4; i += 2) {
			drawSketchRect(ctx, x + i * bw, y - 10, bw, 10, 1.5);
		}
		// Cross-hatch stonework
		ctx.strokeStyle = "rgba(50, 30, 10, 0.15)";
		ctx.lineWidth = 0.8;
		for (let row = 0; row < height; row += 12) {
			const offset = row % 24 === 0 ? 0 : width / 6;
			for (let col = offset; col < width; col += width / 3) {
				ctx.beginPath();
				ctx.moveTo(x + col, y + row);
				ctx.lineTo(x + col, y + Math.min(row + 12, height));
				ctx.stroke();
			}
			drawInkLine(ctx, x, y + row, x + width, y + row, 0.8, 0.2);
		}
		// Window
		drawSketchRect(ctx, x + width / 2 - 6, y + height * 0.3, 12, 16, 1.2);
	} else if (type === "flag") {
		// Flag pole
		drawInkLine(ctx, x + width / 2, y, x + width / 2, groundY, 2, 0.7);
		// Flag
		ctx.fillStyle = hit ? "rgba(50, 30, 10, 0.1)" : "rgba(150, 40, 30, 0.3)";
		ctx.beginPath();
		ctx.moveTo(x + width / 2, y);
		ctx.lineTo(x + width / 2 + 30, y + 12);
		ctx.lineTo(x + width / 2, y + 24);
		ctx.closePath();
		ctx.fill();
		ctx.strokeStyle = "rgba(50, 30, 10, 0.6)";
		ctx.lineWidth = 1.5;
		ctx.stroke();
	} else if (type === "wall") {
		drawSketchRect(ctx, x, y, width, height, 2.5);
		// Brick pattern
		ctx.strokeStyle = "rgba(50, 30, 10, 0.15)";
		ctx.lineWidth = 0.8;
		for (let row = 0; row < height; row += 10) {
			drawInkLine(ctx, x, y + row, x + width, y + row, 0.8, 0.15);
			const offset = row % 20 === 0 ? 0 : width / 5 / 2;
			for (let col = offset; col < width; col += width / 5) {
				ctx.beginPath();
				ctx.moveTo(x + col, y + row);
				ctx.lineTo(x + col, y + Math.min(row + 10, height));
				ctx.stroke();
			}
		}
	}

	if (hit) {
		// Draw X over hit target
		const cx = x + width / 2;
		const cy = y + height / 2;
		const s = Math.max(width, height) * 0.5;
		ctx.globalAlpha = 0.7;
		drawInkLine(ctx, cx - s, cy - s, cx + s, cy + s, 3, 0.7);
		drawInkLine(ctx, cx + s, cy - s, cx - s, cy + s, 3, 0.7);
	}

	ctx.restore();
}

// ─── Projectile Drawing ─────────────────────────────────────────────────────

function drawProjectile(ctx: CanvasRenderingContext2D, proj: Projectile) {
	// Trail
	ctx.save();
	if (proj.trail.length > 1) {
		ctx.strokeStyle = "rgba(50, 30, 10, 0.15)";
		ctx.lineWidth = 1;
		ctx.setLineDash([4, 6]);
		ctx.beginPath();
		ctx.moveTo(proj.trail[0]!.x, proj.trail[0]!.y);
		for (let i = 1; i < proj.trail.length; i++) {
			ctx.lineTo(proj.trail[i]!.x, proj.trail[i]!.y);
		}
		ctx.stroke();
		ctx.setLineDash([]);

		// Da Vinci style arc notation dots
		for (let i = 0; i < proj.trail.length; i += 6) {
			const p = proj.trail[i]!;
			ctx.fillStyle = "rgba(50, 30, 10, 0.3)";
			ctx.beginPath();
			ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
			ctx.fill();
		}
	}
	ctx.restore();

	// Stone projectile
	if (!proj.landed) {
		drawInkCircle(ctx, proj.x, proj.y, proj.radius, 2, true);
		// Impact marks on stone
		ctx.save();
		ctx.fillStyle = "rgba(50, 30, 10, 0.3)";
		for (let i = 0; i < 3; i++) {
			const a = (i / 3) * Math.PI * 2 + 0.5;
			ctx.beginPath();
			ctx.arc(
				proj.x + Math.cos(a) * proj.radius * 0.5,
				proj.y + Math.sin(a) * proj.radius * 0.5,
				1.5,
				0,
				Math.PI * 2
			);
			ctx.fill();
		}
		ctx.restore();
	} else {
		// Impact crater
		ctx.save();
		ctx.strokeStyle = "rgba(50, 30, 10, 0.4)";
		ctx.lineWidth = 1.5;
		ctx.beginPath();
		ctx.ellipse(proj.x, proj.y, proj.radius * 1.5, proj.radius * 0.6, 0, 0, Math.PI * 2);
		ctx.stroke();

		// Debris lines
		for (let i = 0; i < 6; i++) {
			const a = Math.PI + (Math.random() - 0.5) * Math.PI;
			const len = 8 + Math.random() * 15;
			drawInkLine(
				ctx,
				proj.x,
				proj.y,
				proj.x + Math.cos(a) * len,
				proj.y + Math.sin(a) * len,
				1,
				0.3
			);
		}
		ctx.restore();
	}
}

// ─── Generate Targets ────────────────────────────────────────────────────────

function generateTargets(canvasWidth: number, groundY: number): Target[] {
	const targets: Target[] = [];
	const startX = canvasWidth * 0.45;
	const endX = canvasWidth * 0.92;
	const count = 4 + Math.floor(Math.random() * 3);

	const types: Target["type"][] = ["tower", "wall", "flag"];

	for (let i = 0; i < count; i++) {
		const type = types[i % types.length]!;
		const x = startX + ((endX - startX) / count) * i + Math.random() * 40;

		let width: number, height: number;
		if (type === "tower") {
			width = 35 + Math.random() * 15;
			height = 60 + Math.random() * 40;
		} else if (type === "wall") {
			width = 50 + Math.random() * 30;
			height = 30 + Math.random() * 20;
		} else {
			width = 20;
			height = 50 + Math.random() * 30;
		}

		targets.push({
			x,
			y: groundY - height,
			width,
			height,
			hit: false,
			type,
		});
	}

	return targets;
}

// ─── Wind Indicator ──────────────────────────────────────────────────────────

function drawWindIndicator(ctx: CanvasRenderingContext2D, wind: number, x: number, y: number) {
	drawMirroredText(ctx, "OTNEVLA", x, y - 18, 11, true);
	drawMirroredText(ctx, "(Wind)", x, y - 18, 11, false);

	const arrowLen = Math.abs(wind) * 40;
	const dir = wind > 0 ? 1 : -1;

	if (Math.abs(wind) > 0.05) {
		drawInkLine(ctx, x - arrowLen * dir, y, x + arrowLen * dir, y, 2, 0.6);
		// Arrow head
		drawInkLine(
			ctx,
			x + arrowLen * dir,
			y,
			x + arrowLen * dir - 8 * dir,
			y - 5,
			2,
			0.6
		);
		drawInkLine(
			ctx,
			x + arrowLen * dir,
			y,
			x + arrowLen * dir - 8 * dir,
			y + 5,
			2,
			0.6
		);
	} else {
		// Calm indicator
		drawMirroredText(ctx, "~calm~", x, y, 11, false);
	}
}

// ─── Leonardo's Notes ────────────────────────────────────────────────────────

function drawLeonardoNotes(
	ctx: CanvasRenderingContext2D,
	w: number,
	h: number,
	armLength: number,
	tension: number,
	score: number,
	totalShots: number
) {
	// Top right - mirrored title
	ctx.save();
	ctx.font = "italic 28px 'Georgia', 'Times New Roman', serif";
	ctx.fillStyle = "rgba(50, 30, 10, 0.6)";
	ctx.textAlign = "center";

	// Mirrored title
	ctx.save();
	ctx.translate(w / 2, 38);
	ctx.scale(-1, 1);
	ctx.fillText("ATLUPATAC ICNIV AD", 0, 0);
	ctx.restore();

	// Normal subtitle
	ctx.font = "italic 15px 'Georgia', 'Times New Roman', serif";
	ctx.fillStyle = "rgba(50, 30, 10, 0.45)";
	ctx.fillText("Da Vinci Catapult — Torsion Spring Engine", w / 2, 60);
	ctx.restore();

	// Technical notes - top left
	const noteX = 24;
	const noteY = 90;
	drawMirroredText(ctx, `:oizzarB led hccozzarB - ${Math.round(armLength * 100)}`, noteX + 100, noteY, 12, true);
	drawMirroredText(ctx, `Arm Length: ${Math.round(armLength * 100)}%`, noteX + 100, noteY, 12, false);

	drawMirroredText(ctx, `:enoisroT - ${Math.round(tension * 100)}`, noteX + 100, noteY + 20, 12, true);
	drawMirroredText(ctx, `Torsion: ${Math.round(tension * 100)}%`, noteX + 100, noteY + 20, 12, false);

	// Score in top right
	const scoreX = w - 120;
	drawMirroredText(ctx, `OIGGETTNUP: ${score}/${totalShots}`, scoreX, noteY, 14, true);
	drawMirroredText(ctx, `Score: ${score} / ${totalShots} shots`, scoreX, noteY, 14, false);

	// Decorative corner flourishes
	ctx.save();
	ctx.strokeStyle = "rgba(50, 30, 10, 0.2)";
	ctx.lineWidth = 1;

	// Top left corner
	ctx.beginPath();
	ctx.moveTo(10, 30);
	ctx.quadraticCurveTo(10, 10, 30, 10);
	ctx.stroke();
	ctx.beginPath();
	ctx.moveTo(10, 35);
	ctx.quadraticCurveTo(12, 12, 35, 10);
	ctx.stroke();

	// Top right corner
	ctx.beginPath();
	ctx.moveTo(w - 10, 30);
	ctx.quadraticCurveTo(w - 10, 10, w - 30, 10);
	ctx.stroke();
	ctx.beginPath();
	ctx.moveTo(w - 10, 35);
	ctx.quadraticCurveTo(w - 12, 12, w - 35, 10);
	ctx.stroke();
	ctx.restore();
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function DaVinciCatapult() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const parchmentCacheRef = useRef<HTMLCanvasElement | null>(null);
	const animFrameRef = useRef<number>(0);
	const lastTimeRef = useRef<number>(0);

	const [armLength, setArmLength] = useState(0.6);
	const [tension, setTension] = useState(0.5);
	const [score, setScore] = useState(0);
	const [totalShots, setTotalShots] = useState(0);
	const [message, setMessage] = useState("Adjust arm & tension, then FIRE!");

	const stateRef = useRef<GameState>({
		armLength: 0.6,
		tension: 0.5,
		armAngle: LOADED_ANGLE,
		isLoaded: true,
		isFiring: false,
		projectile: null,
		targets: [],
		score: 0,
		totalShots: 0,
		wind: (Math.random() - 0.5) * 0.6,
		groundY: 0,
	});

	const initTargets = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		stateRef.current.targets = generateTargets(canvas.width, stateRef.current.groundY);
		stateRef.current.wind = (Math.random() - 0.5) * 0.6;
	}, []);

	const fire = useCallback(() => {
		const state = stateRef.current;
		if (!state.isLoaded || state.isFiring) return;
		state.isFiring = true;
		state.isLoaded = false;
	}, []);

	const reload = useCallback(() => {
		const state = stateRef.current;
		state.armAngle = LOADED_ANGLE;
		state.isLoaded = true;
		state.isFiring = false;
		state.projectile = null;
		setMessage("Adjust arm & tension, then FIRE!");
	}, []);

	const resetTargets = useCallback(() => {
		initTargets();
		reload();
		setScore(0);
		setTotalShots(0);
		stateRef.current.score = 0;
		stateRef.current.totalShots = 0;
		setMessage("New targets placed! Adjust and FIRE!");
	}, [initTargets, reload]);

	// Sync slider values to state ref
	useEffect(() => {
		stateRef.current.armLength = armLength;
	}, [armLength]);

	useEffect(() => {
		stateRef.current.tension = tension;
	}, [tension]);

	// Keyboard controls
	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.code === "Space" || e.key === " ") {
				e.preventDefault();
				fire();
			} else if (e.key === "r" || e.key === "R") {
				reload();
			} else if (e.key === "n" || e.key === "N") {
				resetTargets();
			}
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [fire, reload, resetTargets]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const resize = () => {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
			stateRef.current.groundY = canvas.height - 100;

			// Recreate parchment cache
			const pCache = document.createElement("canvas");
			pCache.width = canvas.width;
			pCache.height = canvas.height;
			const pCtx = pCache.getContext("2d")!;
			drawParchment(pCtx, pCache.width, pCache.height);
			parchmentCacheRef.current = pCache;

			if (stateRef.current.targets.length === 0) {
				stateRef.current.targets = generateTargets(canvas.width, stateRef.current.groundY);
			}
		};

		resize();
		window.addEventListener("resize", resize);

		const ctx = canvas.getContext("2d")!;

		const gameLoop = (time: number) => {
			const dt = Math.min((time - lastTimeRef.current) / 1000, 0.05);
			lastTimeRef.current = time;

			const state = stateRef.current;
			const w = canvas.width;
			const h = canvas.height;
			const groundY = state.groundY;
			const pivotX = w * ARM_PIVOT_X_RATIO;
			const actualArmLength = BASE_ARM_LENGTH + (MAX_ARM_LENGTH - BASE_ARM_LENGTH) * state.armLength;

			// ─── Physics Update ───

			if (state.isFiring && !state.projectile) {
				const angularVelocity = ANGULAR_VELOCITY_BASE * (0.5 + state.tension * 1.5);
				state.armAngle -= angularVelocity * dt;

				if (state.armAngle <= RELEASE_ANGLE) {
					state.armAngle = RELEASE_ANGLE;
					state.isFiring = false;

					// Calculate release velocity
					const pivotY = groundY - ARM_PIVOT_Y_OFFSET - 90 + 5;
					const endX = pivotX + Math.cos(state.armAngle) * actualArmLength;
					const endY = pivotY + Math.sin(state.armAngle) * actualArmLength;

					const speed = angularVelocity * actualArmLength * 1.1;
					const releaseDir = state.armAngle - Math.PI / 2;

					state.projectile = {
						x: endX,
						y: endY,
						vx: Math.cos(releaseDir) * speed + state.wind * 50,
						vy: Math.sin(releaseDir) * speed,
						radius: 8,
						trail: [{ x: endX, y: endY }],
						active: true,
						landed: false,
					};

					state.totalShots++;
					setTotalShots(state.totalShots);
				}
			}

			if (state.projectile && state.projectile.active && !state.projectile.landed) {
				const proj = state.projectile;
				proj.vy += GRAVITY * dt;
				proj.vx += state.wind * 30 * dt;
				proj.x += proj.vx * dt;
				proj.y += proj.vy * dt;

				// Trail
				if (proj.trail.length === 0 || Math.hypot(proj.x - proj.trail[proj.trail.length - 1]!.x, proj.y - proj.trail[proj.trail.length - 1]!.y) > 8) {
					proj.trail.push({ x: proj.x, y: proj.y });
				}

				// Ground collision
				if (proj.y >= groundY - proj.radius) {
					proj.y = groundY - proj.radius;
					proj.landed = true;
					proj.active = false;
					setMessage("Missed! Reload and try again.");
				}

				// Off screen
				if (proj.x > w + 50 || proj.x < -50) {
					proj.landed = true;
					proj.active = false;
					setMessage("Shot went off screen! Reload.");
				}

				// Target collision
				for (const target of state.targets) {
					if (target.hit) continue;
					if (
						proj.x + proj.radius > target.x &&
						proj.x - proj.radius < target.x + target.width &&
						proj.y + proj.radius > target.y &&
						proj.y - proj.radius < target.y + target.height
					) {
						target.hit = true;
						proj.landed = true;
						proj.active = false;
						state.score++;
						setScore(state.score);
						setMessage(`HIT! Target destroyed! (${state.score} hits)`);

						// Check all targets hit
						if (state.targets.every((t) => t.hit)) {
							setMessage(`All targets destroyed in ${state.totalShots} shots! Reset for new round.`);
						}
						break;
					}
				}
			}

			// ─── Rendering ───

			ctx.clearRect(0, 0, w, h);

			// Parchment background (cached)
			if (parchmentCacheRef.current) {
				ctx.drawImage(parchmentCacheRef.current, 0, 0);
			}

			// Ground
			drawGround(ctx, w, groundY);

			// Targets
			for (const target of state.targets) {
				drawTarget(ctx, target, groundY);
			}

			// Distance markers
			ctx.save();
			ctx.strokeStyle = "rgba(50, 30, 10, 0.2)";
			ctx.lineWidth = 1;
			ctx.setLineDash([3, 8]);
			for (let d = 0.3; d <= 0.9; d += 0.15) {
				const mx = w * d;
				ctx.beginPath();
				ctx.moveTo(mx, groundY - 5);
				ctx.lineTo(mx, groundY + 5);
				ctx.stroke();
				drawMirroredText(ctx, `${Math.round(d * 100)}m`, mx, groundY + 18, 10, false);
			}
			ctx.setLineDash([]);
			ctx.restore();

			// Catapult
			drawCatapult(ctx, pivotX, groundY, actualArmLength, state.armAngle, state.tension, state.isLoaded);

			// Projectile
			if (state.projectile) {
				drawProjectile(ctx, state.projectile);
			}

			// Wind indicator
			drawWindIndicator(ctx, state.wind, w / 2, h - 45);

			// Leonardo's notes and labels
			drawLeonardoNotes(ctx, w, h, state.armLength, state.tension, state.score, state.totalShots);

			// Technical diagram annotations
			const pivotY = groundY - ARM_PIVOT_Y_OFFSET - 90 + 5;
			drawMirroredText(ctx, "otrep onrep", pivotX, pivotY - 25, 10, true);
			drawMirroredText(ctx, "(pivot point)", pivotX, pivotY - 25, 10, false);

			// Force vector visualization
			if (state.isLoaded) {
				ctx.save();
				ctx.strokeStyle = "rgba(150, 40, 30, 0.3)";
				ctx.lineWidth = 1;
				ctx.setLineDash([4, 4]);

				const forceLen = 30 + state.tension * 50;
				const armEndX = pivotX + Math.cos(state.armAngle) * actualArmLength;
				const armEndY = pivotY + Math.sin(state.armAngle) * actualArmLength;
				const forceAngle = state.armAngle - Math.PI / 2;

				ctx.beginPath();
				ctx.moveTo(armEndX, armEndY);
				ctx.lineTo(
					armEndX + Math.cos(forceAngle) * forceLen,
					armEndY + Math.sin(forceAngle) * forceLen
				);
				ctx.stroke();

				// Arrow head
				const endFX = armEndX + Math.cos(forceAngle) * forceLen;
				const endFY = armEndY + Math.sin(forceAngle) * forceLen;
				ctx.beginPath();
				ctx.moveTo(endFX, endFY);
				ctx.lineTo(
					endFX - Math.cos(forceAngle - 0.4) * 8,
					endFY - Math.sin(forceAngle - 0.4) * 8
				);
				ctx.moveTo(endFX, endFY);
				ctx.lineTo(
					endFX - Math.cos(forceAngle + 0.4) * 8,
					endFY - Math.sin(forceAngle + 0.4) * 8
				);
				ctx.stroke();
				ctx.setLineDash([]);
				ctx.restore();

				drawMirroredText(ctx, "F = kθ", armEndX + Math.cos(forceAngle) * (forceLen + 18), armEndY + Math.sin(forceAngle) * (forceLen + 18), 11, false);
			}

			animFrameRef.current = requestAnimationFrame(gameLoop);
		};

		animFrameRef.current = requestAnimationFrame(gameLoop);

		return () => {
			cancelAnimationFrame(animFrameRef.current);
			window.removeEventListener("resize", resize);
		};
	}, []);

	return (
		<div className="fixed inset-0 overflow-hidden" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
			<canvas
				ref={canvasRef}
				className="absolute inset-0 w-full h-full"
				style={{ cursor: "crosshair" }}
			/>

			{/* Controls Panel - Renaissance styled */}
			<div
				className="absolute bottom-4 left-4 p-4 rounded-sm"
				style={{
					background: "rgba(228, 210, 170, 0.92)",
					border: "2px solid rgba(50, 30, 10, 0.3)",
					boxShadow: "2px 2px 8px rgba(50, 30, 10, 0.15)",
					minWidth: 280,
				}}
			>
				{/* Message */}
				<div
					className="text-center mb-3 text-sm italic"
					style={{ color: "rgba(50, 30, 10, 0.7)" }}
				>
					{message}
				</div>

				{/* Arm Length */}
				<div className="mb-3">
					<div className="flex justify-between items-center mb-1">
						<label
							className="text-xs italic"
							style={{ color: "rgba(50, 30, 10, 0.6)" }}
						>
							<span style={{ display: "inline-block", transform: "scaleX(-1)" }}>
								mra fo htgneL
							</span>
							{" "}— Arm Length
						</label>
						<span
							className="text-xs font-bold"
							style={{ color: "rgba(50, 30, 10, 0.8)" }}
						>
							{Math.round(armLength * 100)}%
						</span>
					</div>
					<input
						type="range"
						min={30}
						max={100}
						value={Math.round(armLength * 100)}
						onChange={(e) => setArmLength(Number(e.target.value) / 100)}
						className="w-full h-2 rounded-full appearance-none"
						style={{
							background: `linear-gradient(to right, rgba(50,30,10,0.5) ${armLength * 100}%, rgba(50,30,10,0.15) ${armLength * 100}%)`,
							accentColor: "rgb(100, 60, 20)",
						}}
						disabled={stateRef.current.isFiring}
					/>
				</div>

				{/* Tension */}
				<div className="mb-3">
					<div className="flex justify-between items-center mb-1">
						<label
							className="text-xs italic"
							style={{ color: "rgba(50, 30, 10, 0.6)" }}
						>
							<span style={{ display: "inline-block", transform: "scaleX(-1)" }}>
								noisroT gnirpS
							</span>
							{" "}— Spring Torsion
						</label>
						<span
							className="text-xs font-bold"
							style={{ color: "rgba(50, 30, 10, 0.8)" }}
						>
							{Math.round(tension * 100)}%
						</span>
					</div>
					<input
						type="range"
						min={30}
						max={100}
						value={Math.round(tension * 100)}
						onChange={(e) => setTension(Number(e.target.value) / 100)}
						className="w-full h-2 rounded-full appearance-none"
						style={{
							background: `linear-gradient(to right, rgba(150,40,30,0.5) ${tension * 100}%, rgba(50,30,10,0.15) ${tension * 100}%)`,
							accentColor: "rgb(150, 40, 30)",
						}}
						disabled={stateRef.current.isFiring}
					/>
				</div>

				{/* Buttons */}
				<div className="flex gap-2">
					<button
						onClick={fire}
						disabled={!stateRef.current.isLoaded || stateRef.current.isFiring}
						className="flex-1 py-2 px-4 rounded-sm text-sm font-bold italic uppercase tracking-wider transition-all"
						style={{
							background: stateRef.current.isLoaded
								? "rgba(150, 40, 30, 0.8)"
								: "rgba(50, 30, 10, 0.2)",
							color: stateRef.current.isLoaded
								? "rgba(255, 240, 210, 0.95)"
								: "rgba(50, 30, 10, 0.4)",
							border: "1px solid rgba(50, 30, 10, 0.3)",
							cursor: stateRef.current.isLoaded ? "pointer" : "not-allowed",
						}}
					>
						FIRE
					</button>
					<button
						onClick={reload}
						className="flex-1 py-2 px-4 rounded-sm text-sm italic tracking-wider transition-all"
						style={{
							background: "rgba(50, 30, 10, 0.15)",
							color: "rgba(50, 30, 10, 0.8)",
							border: "1px solid rgba(50, 30, 10, 0.3)",
							cursor: "pointer",
						}}
					>
						Reload
					</button>
					<button
						onClick={resetTargets}
						className="py-2 px-3 rounded-sm text-sm italic tracking-wider transition-all"
						style={{
							background: "rgba(50, 30, 10, 0.1)",
							color: "rgba(50, 30, 10, 0.6)",
							border: "1px solid rgba(50, 30, 10, 0.2)",
							cursor: "pointer",
						}}
					>
						New
					</button>
				</div>

				{/* Keyboard hint */}
				<div
					className="text-center mt-2 text-xs italic"
					style={{ color: "rgba(50, 30, 10, 0.4)" }}
				>
					Space to fire · R to reload · N for new targets
				</div>
			</div>

			{/* Da Vinci signature area */}
			<div
				className="absolute bottom-4 right-4 text-right italic text-xs"
				style={{ color: "rgba(50, 30, 10, 0.35)" }}
			>
				<div style={{ transform: "scaleX(-1)", display: "inline-block" }}>
					icniV ad odranoeL
				</div>
				<br />
				<span>Leonardo da Vinci</span>
				<br />
				<span style={{ fontSize: 10 }}>Codex Atlanticus, folio 141r</span>
			</div>
		</div>
	);
}

export default DaVinciCatapult;
